import React, { useEffect, useState } from "react";
import axios from "axios";
import { BASELINE } from "../../util";

const RNavBar = ({
  skilldesc,
  onSave,
  onToDashboard,
  onComplete,
  onLeaveMemo,
  onToggle,
  onNext,
  onBack,
  questionIds,
  id
}) => {
  // Define hotkeys for various actions.
  const hotkeys = {
    save: "Q",            // Save: Ctrl+Q
    toDashboard: "M",     // To Dashboard: Ctrl+M
    markComplete: "Z",    // Mark Complete: Ctrl+Z
    leaveMemo: "X",       // Leave Memo: Ctrl+X
    list: "L",            // List: Ctrl+L
    next: "ArrowRight",   // Next: Ctrl+→
    back: "ArrowLeft",    // Back: Ctrl+←
    memo: "Y"
  };

  // State for controlling the upload video modal and storing the selected file.
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);

  // State for controlling the view video modal.
  const [showVideoViewModal, setShowVideoViewModal] = useState(false);

  const [showMemo, setShowMemo] = useState(false);
  const [memoList, setMemoList] = useState([]);
  // Escape key handler for the upload video modal.
  useEffect(() => {
    if (showVideoModal) {
      const handleEsc = (e) => {
        if (e.key === "Escape") {
          setShowVideoModal(false);
          setSelectedVideo(null);
        }
      };
      window.addEventListener("keydown", handleEsc);
      return () => window.removeEventListener("keydown", handleEsc);
    }
  }, [showVideoModal]);

  const fetchMemoList = () => {
    axios
      .get(BASELINE + "get/note", { params: { questionIds } })
      .then((response) => {
        if (response.data.success) {
          setMemoList(response.data.notes);
          setShowMemo((prevState) => !prevState);
        } else {
          console.error("Failed to fetch memo list.");
        }
      })
      .catch((error) => {
        console.error("Error fetching memo list:", error);
      });
  };

  // Escape key handler for the view video modal.
  useEffect(() => {
    if (showVideoViewModal) {
      const handleEsc = (e) => {
        if (e.key === "Escape") {
          setShowVideoViewModal(false);
        }
      };
      window.addEventListener("keydown", handleEsc);
      return () => window.removeEventListener("keydown", handleEsc);
    }
  }, [showVideoViewModal]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const isCmd = isMac ? e.metaKey : e.ctrlKey;

      if (isCmd) {
        const key = e.key.toUpperCase();
        if (key === hotkeys.save) {
          e.preventDefault();
          onSave && onSave();
        } else if (key === hotkeys.toDashboard) {
          e.preventDefault();
          onToDashboard && onToDashboard();
        } else if (key === hotkeys.markComplete) {
          e.preventDefault();
          onComplete && onComplete();
        } else if (key === hotkeys.leaveMemo) {
          e.preventDefault();
          onLeaveMemo && onLeaveMemo();
        } else if (key === hotkeys.list) {
          e.preventDefault();
          onToggle && onToggle();
        } else if (key === hotkeys.memo) {
          e.preventDefault();
          fetchMemoList();
        }
      }

      if (isCmd && e.key === hotkeys.next) {
        e.preventDefault();
        onNext && onNext();
      }
      if (isCmd && e.key === hotkeys.back) {
        e.preventDefault();
        onBack && onBack();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onSave, onToDashboard, onComplete, onLeaveMemo, onToggle, onNext, onBack]);


  // Handle file input change for video upload.
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      setSelectedVideo(e.target.files[0]);
    }
  };

  // Helper function to get current LA time in 'YYYY-MM-DD HH:MM:SS' format.
  const getLATimeString = () => {
    const options = { timeZone: "America/Los_Angeles", hour12: false };
    const date = new Date().toLocaleDateString("en-CA", { timeZone: "America/Los_Angeles" });
    const time = new Date().toLocaleTimeString("en-US", {
      timeZone: "America/Los_Angeles",
      hour12: false,
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
    return `${date} ${time}`;
  };

  const handleVideoUpload = () => {
    if (!selectedVideo) {
      alert("Please select a video file.");
      return;
    }
    const maxSize = 300 * 1024 * 1024; // 300MB in bytes.
    if (selectedVideo.size > maxSize) {
      alert("Video file exceeds 300MB. Please select a smaller file.");
      return;
    }

    // Check if a video already exists for the current question.
    axios
      .get(BASELINE + "video/exists", { params: { question_id: questionIds } })
      .then((response) => {
        const videoExists = response.data.exists;
        if (videoExists) {
          if (
            !window.confirm(
              "A video already exists and will be replaced. Do you want to continue?"
            )
          ) {
            return; // Stop if the user cancels.
          }
        }
        // Proceed with the upload (either replacing or new).
        uploadVideo(videoExists);
      })
      .catch((error) => {
        if (error.response && error.response.status === 404) {
          uploadVideo(false);
        } else {
          console.error("Error checking video existence:", error);
          alert("Could not verify if a video exists. Please try again later.");
        }
      });
  };

  const uploadVideo = (videoExists) => {
    const formData = new FormData();
    formData.append("question_id", questionIds);
    formData.append("video", selectedVideo);

    axios
      .post(BASELINE + "upload/video", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((uploadResponse) => {
        if (uploadResponse.data.success) {
          alert("Video uploaded successfully.");
          const currentLATime = getLATimeString();
          const logMessage = videoExists
            ? `${id} replace the video to the ${questionIds} at ${currentLATime}`
            : `${id} upload the video to the ${questionIds} at ${currentLATime}`;
          axios
            .post(BASELINE + "add/log", {
              uid: id,
              question_id: questionIds,
              log: logMessage,
              currentLATime: currentLATime,
            })
            .then((logResponse) => {
              if (logResponse.data.success) {
              } else {
                console.error("Failed to save log.");
              }
            })
            .catch((error) => {
              console.error("Error saving log:", error);
            });
          setShowVideoModal(false);
          setSelectedVideo(null);
        } else {
          alert("Video upload failed.");
        }
      })
      .catch((error) => {
        console.error("Error uploading video:", error);
        alert("An error occurred during video upload.");
      });
  };

  // Handler for viewing video: log the view event and open the video view modal.
  const handleViewVideo = () => {
    const currentLATime = getLATimeString();
    const logMessage = `${id} viewed the video for ${questionIds} at ${currentLATime}`;
    axios
      .post(BASELINE + "add/log", {
        uid: id,
        question_id: questionIds,
        log: logMessage,
        currentLATime: currentLATime,
      })
      .then((logResponse) => {
        if (logResponse.data.success) {
        } else {
          console.error("Failed to save view log.");
        }
      })
      .catch((error) => {
        console.error("Error saving view log:", error);
      });
    setShowVideoViewModal(true);
  };

  // Render the video upload modal.
  const renderVideoModal = () => (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <div className="bg-white p-4 rounded shadow-lg w-[80%] max-w-lg">
        <h3 className="text-black text-xl font-bold mb-4">Upload Video</h3>
        <input
          type="file"
          accept="video/*"
          onChange={handleFileChange}
          className="mb-4"
        />
        <div className="flex justify-end gap-2">
          <button
            onClick={() => {
              setShowVideoModal(false);
              setSelectedVideo(null);
            }}
            className="bg-gray-500 text-white p-2 rounded"
          >
            Close
          </button>
          <button
            onClick={handleVideoUpload}
            className="bg-green-500 text-white p-2 rounded"
          >
            Upload
          </button>
        </div>
      </div>
    </div>
  );

  // Render the video view modal.
  const renderVideoViewModal = () => (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <div className="bg-white p-4 rounded shadow-lg w-[80%] max-w-2xl">
        <h3 className="text-black text-xl font-bold mb-4">View Video</h3>
        <video
          controls
          className="w-full"
          src={`${BASELINE}view/video?question_id=${questionIds}`}
        >
          Your browser does not support the video tag.
        </video>
        <div className="flex justify-end mt-4">
          <button
            onClick={() => setShowVideoViewModal(false)}
            className="bg-gray-500 text-white p-2 rounded"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );

  const logAction = (actionMessage) => {
    const currentLATime = getLATimeString();
    const logMessage = `${id} ${actionMessage} for ${questionIds} at ${currentLATime}`;

    return axios.post(BASELINE + "add/log", {
      uid: id,
      question_id: questionIds,
      log: logMessage,
      currentLATime: currentLATime,
    });
  };

  const deleteMemo = (noteId) => {
    // Ask for confirmation before deleting the memo
    const confirmDelete = window.confirm('Are you sure you want to delete this note?');
  
    // Early return if the user cancels the deletion
    if (!confirmDelete) {
      return;
    }
  
    // Log the action before proceeding
    logAction('remove the memo')
      .then((logResponse) => {
        if (!logResponse.data.success) {
          console.error("Failed to save view log.");
        }
      })
      .catch((error) => {
        console.error("Error saving view log:", error);
      });
  
    // Proceed with deleting the memo from the backend
    axios.delete(`${BASELINE}delete/note`, { params: { noteId } })
      .then((response) => {
        if (response.data.success) {
          // Remove the deleted memo from the list immediately
          setShowMemo((prevState) => !prevState);
          setMemoList((prevMemoList) =>
            prevMemoList.filter((memo) => memo.note.id !== noteId)
          );
        } else {
          console.error("Failed to delete memo");
        }
      })
      .catch((error) => {
        console.error("Error deleting memo:", error);
      });
  };
  
  const renderMemoSidebar = () => (
    <div className="z-[100] fixed top-0 right-0 bg-white w-1/5 h-full shadow-lg p-4">
      <h3 className="text-black text-xl font-bold mb-4">Memo List</h3>
      <ul>
        {memoList.length > 0 ? (
          memoList.map((memo, index) => {
            return (
              <li key={index} className="mb-2 bg-gray-300 border border-2 text-black">
                <p><strong>User ID:</strong> {memo.note.userid || "No user ID available"}</p>
                <p><strong>Note:</strong> {memo.note.note || "No note available"}</p>
                {/* Add the delete button */}
                <button
                  onClick={() => deleteMemo(memo.id)}
                  className="bg-red-500 text-white p-1 rounded mt-2"
                >
                  Delete
                </button>
              </li>
            );
          })
        ) : (
          <li>No memos available.</li>
        )}
      </ul>
    </div>
  );

  return (
    <nav className="bg-slate-700 text-white p-4 flex justify-between items-center">
      {/* Left section: SAT text, skilldesc, and List button */}
      <div className="flex items-center gap-4">
        <div className="text-xl font-bold">SAT</div>
        <div
          onClick={() => {
            navigator.clipboard
              .writeText(questionIds)
              .then(() => alert("Question IDs copied to clipboard!"))
              .catch((err) => alert("Failed to copy text: " + err));
          }}
          className="cursor-pointer"
        >
          ID: {questionIds}
        </div>
        <button className="bg-slate-600 p-2 rounded-lg" onClick={onToggle}>
          List [{hotkeys.list}]
        </button>
        <button className="bg-slate-600 p-2 rounded-lg" onClick={() => fetchMemoList()}>
          View Memo [{hotkeys.memo}]
        </button>
      </div>
      {/* Right section: function buttons */}
      <div className="flex gap-4">
        <button
          className="bg-slate-600 p-2 rounded-lg"
          onClick={handleViewVideo}
        >
          View Video
        </button>
        <button
          className="bg-slate-600 p-2 rounded-lg"
          onClick={() => setShowVideoModal(true)}
        >
          Upload Video
        </button>
        <button onClick={onSave} className="bg-slate-600 p-2 rounded-lg">
          Save [{hotkeys.save}]
        </button>
        <button onClick={onToDashboard} className="bg-slate-600 p-2 rounded-lg">
          To Dashboard [{hotkeys.toDashboard}]
        </button>
        <button onClick={onComplete} className="bg-slate-600 p-2 rounded-lg">
          Mark Complete [{hotkeys.markComplete}]
        </button>
        <button onClick={onLeaveMemo} className="bg-slate-600 p-2 rounded-lg">
          Leave Memo [{hotkeys.leaveMemo}]
        </button>
        <button onClick={onBack} className="bg-slate-600 p-2 rounded-lg">
          Back [←]
        </button>
        <button onClick={onNext} className="bg-slate-600 p-2 rounded-lg">
          Next [→]
        </button>
      </div>
      {/* Conditionally render the video modals */}
      {showVideoModal && renderVideoModal()}
      {showVideoViewModal && renderVideoViewModal()}
      {showMemo && renderMemoSidebar()}
    </nav>
  );
};

export default RNavBar;
